/* CSV export with column picker — Xiaomi benefit outcomes (LIVE 2026-06-02)
 *
 * Lets Ayush / Dipali pull out arbitrary slices:
 *   • "partner name + written feedback only"
 *   • "anniversary status / where they went / experience date"
 *   • full audit dump
 */
import { useEffect, useState } from 'react';
import { Download, X, FileSpreadsheet } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const COLUMN_LABELS = {
  retailer_id: 'Retailer ID',
  outlet_name: 'Outlet name',
  owner_name: 'Owner name',
  tier: 'Tier',
  city: 'City',
  state: 'State',
  zone: 'Zone',
  phone_primary: 'Phone',
  benefit_id: 'Benefit ID',
  month_ym: 'Month',
  status: 'Status',
  status_at: 'Status updated at',
  status_by: 'Status updated by',
  option_chosen: 'Option chosen (where they went)',
  experience_date: 'Experience date',
  venue_name: 'Venue name',
  venue_city: 'Venue city',
  feedback_text: 'Written feedback',
  feedback_at: 'Feedback at',
  feedback_by: 'Feedback by',
  media_count: '# media',
  photo_count: '# photos',
  video_count: '# videos',
  media_urls: 'Media URLs',
  updated_at: 'Updated at',
};

const PRESETS = [
  { id: 'name_feedback',
    label: 'Partner name + written feedback only',
    cols: ['outlet_name', 'owner_name', 'feedback_text'] },
  { id: 'anniversary_status',
    label: 'Anniversary · where they went, status, done/not done',
    cols: ['outlet_name', 'owner_name', 'tier', 'status',
            'option_chosen', 'experience_date', 'venue_name', 'venue_city'] },
  { id: 'full', label: 'Everything (audit-grade)', cols: null },
  { id: 'custom', label: 'Custom · pick columns', cols: [] },
];

export default function OutcomeExportButton({
  benefitId = '', monthYm = '', status = '', label = 'Export CSV',
  defaultPreset = 'name_feedback',
}) {
  const [open, setOpen] = useState(false);
  const [available, setAvailable] = useState([]);
  const [defaultCols, setDefaultCols] = useState([]);
  const [presetId, setPresetId] = useState(defaultPreset);
  const [customCols, setCustomCols] = useState(new Set());
  const [filters, setFilters] = useState({ benefitId, monthYm, status });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || available.length > 0) return;
    api.get('/xiaomi/outcomes/columns').then((r) => {
      setAvailable(r.data.available || []);
      setDefaultCols(r.data.default || []);
      setCustomCols(new Set(r.data.default || []));
    }).catch(() => {});
  }, [open, available.length]);

  const resolveCols = () => {
    const p = PRESETS.find((x) => x.id === presetId);
    if (p?.id === 'custom') return [...customCols];
    if (p?.cols === null) return defaultCols;
    return p?.cols || defaultCols;
  };

  const download = async () => {
    setBusy(true);
    try {
      const url = new URL(
        `${process.env.REACT_APP_BACKEND_URL}/api/xiaomi/outcomes/export.csv`);
      const cols = resolveCols();
      if (cols.length > 0) url.searchParams.set('columns', cols.join(','));
      if (filters.benefitId) url.searchParams.set('benefit_id', filters.benefitId);
      if (filters.monthYm) url.searchParams.set('month_ym', filters.monthYm);
      if (filters.status) url.searchParams.set('status', filters.status);
      const r = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const cd = r.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename=([^;]+)/i);
      const filename = m ? m[1].trim().replace(/"/g, '') : 'xiaomi_outcomes.csv';
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(a.href);
      toast.success('Download started');
      setOpen(false);
    } catch (e) {
      toast.error(`Export failed: ${e.message || e}`);
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)}
        className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border bg-white hover:bg-gray-50"
        style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
        data-testid="outcome-export-open">
        <Download size={11} /> {label}
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setOpen(false)} data-testid="outcome-export-modal">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
            <div className="border-b p-4 flex items-center justify-between"
              style={{ borderColor: '#E5E1DC' }}>
              <h3 className="text-lg font-bold inline-flex items-center gap-2"
                style={{ fontFamily: 'Fraunces, serif' }}>
                <FileSpreadsheet size={18} /> Export benefit outcomes CSV
              </h3>
              <button onClick={() => setOpen(false)}
                className="p-1 rounded hover:bg-gray-100"
                data-testid="outcome-export-close">
                <X size={16} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-soft">
                  Quick preset
                </label>
                <div className="grid grid-cols-1 gap-1 mt-1">
                  {PRESETS.map((p) => (
                    <label key={p.id}
                      className="flex items-start gap-2 p-2 rounded border cursor-pointer hover:bg-gray-50"
                      style={{ borderColor: presetId === p.id ? '#1B2A4A' : '#E5E1DC',
                                background: presetId === p.id ? '#F0EEEA' : 'white' }}
                      data-testid={`outcome-preset-${p.id}`}>
                      <input type="radio" checked={presetId === p.id}
                        onChange={() => setPresetId(p.id)}
                        className="mt-0.5" />
                      <span className="text-[12px]">{p.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {presetId === 'custom' && (
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-soft">
                    Custom columns ({customCols.size} selected)
                  </label>
                  <div className="grid grid-cols-2 gap-1 mt-1 max-h-64 overflow-y-auto p-2 border rounded"
                    style={{ borderColor: '#E5E1DC' }}>
                    {available.map((c) => (
                      <label key={c} className="flex items-center gap-1 text-[11px]">
                        <input type="checkbox" checked={customCols.has(c)}
                          onChange={() => setCustomCols((s) => {
                            const next = new Set(s);
                            if (next.has(c)) next.delete(c); else next.add(c);
                            return next;
                          })}
                          data-testid={`outcome-col-${c}`} />
                        {COLUMN_LABELS[c] || c}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-soft">
                    Benefit (optional)
                  </label>
                  <input type="text" value={filters.benefitId}
                    onChange={(e) => setFilters((f) => ({ ...f, benefitId: e.target.value }))}
                    placeholder="e.g. xiaomi_b08_anniversary_staycation"
                    className="w-full mt-1 px-2 py-1 rounded border text-[11px]"
                    style={{ borderColor: '#E5E1DC' }}
                    data-testid="outcome-filter-benefit" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-soft">
                    Month (optional)
                  </label>
                  <input type="text" value={filters.monthYm}
                    onChange={(e) => setFilters((f) => ({ ...f, monthYm: e.target.value }))}
                    placeholder="2026-06"
                    className="w-full mt-1 px-2 py-1 rounded border text-[11px]"
                    style={{ borderColor: '#E5E1DC' }}
                    data-testid="outcome-filter-month" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-soft">
                    Status (optional)
                  </label>
                  <select value={filters.status}
                    onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                    className="w-full mt-1 px-2 py-1 rounded border text-[11px]"
                    style={{ borderColor: '#E5E1DC' }}
                    data-testid="outcome-filter-status">
                    <option value="">Any</option>
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="delivered">Delivered</option>
                    <option value="closed">Closed</option>
                    <option value="declined">Declined</option>
                    <option value="no_response">No response</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="border-t p-3 flex items-center justify-end gap-2"
              style={{ borderColor: '#E5E1DC' }}>
              <button onClick={() => setOpen(false)}
                className="text-[11px] px-3 py-1.5 rounded border bg-white"
                style={{ borderColor: '#E5E1DC' }}>Cancel</button>
              <button onClick={download} disabled={busy}
                className="text-[11px] px-3 py-1.5 rounded text-white font-semibold disabled:opacity-40 inline-flex items-center gap-1"
                style={{ background: '#1B2A4A' }}
                data-testid="outcome-export-download">
                <Download size={11} /> {busy ? 'Building…' : 'Download CSV'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
